/**
 * ServiceDetailPage — Reusable SEO landing-page voor élke dienst.
 *
 * URL pattern: /:slug (bv /ai-seo, /backlinks-laten-bouwen)
 * Data komt uit /src/data/serviceDetails.js
 *
 * Layout:
 *  1. Hero (dark) met H1 + seoIntent tagline + 2 CTAs (gratis scan + diensten-overzicht)
 *  2. Why-blocks (3 cards)
 *  3. What you get (checklist)
 *  4. Uitvoering callout
 *  5. Tier-vergelijking (3 tiers met exacte pricing)
 *  6. FAQ (collapsible)
 *  7. Related services (3 cards)
 *  8. Closing CTA
 *
 * SEO: H1, semantische H2/H3, schema.org Service + FAQPage + BreadcrumbList.
 */
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { getServiceDetail, SERVICE_DETAILS_LIST } from '../data/serviceDetails';
import {
  Search, Target, Globe, MapPin, ShieldCheck, Link2, Wrench, Crown,
  Sparkles, TrendingUp, FileText, BarChart3,
  ArrowRight, CheckCircle2, ChevronRight, Bot, Zap, Headphones,
} from 'lucide-react';

const ICON_MAP = {
  Search, Target, Globe, MapPin, ShieldCheck, Link2, Wrench, Crown,
  Sparkles, TrendingUp, FileText, BarChart3,
};

export default function ServiceDetailPage({ slug }) {
  const service = getServiceDetail(slug);

  if (!service) {
    return <Navigate to="/diensten" replace />;
  }

  const Icon = ICON_MAP[service.iconName] || Search;
  const related = service.relatedSlugs
    .map((s) => SERVICE_DETAILS_LIST.find((d) => d.slug === s))
    .filter(Boolean);

  /* ─── Schema.org structured data ─── */
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.imperiaai.nl/" },
        { "@type": "ListItem", "position": 2, "name": "Diensten", "item": "https://www.imperiaai.nl/diensten" },
        { "@type": "ListItem", "position": 3, "name": service.h1.split('—')[0]?.trim() || service.h1, "item": `https://www.imperiaai.nl/${service.slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": service.h1.split('—')[0]?.trim() || service.h1,
      "description": service.metaDescription,
      "provider": {
        "@type": "Organization",
        "name": "IMPERIA AI",
        "url": "https://www.imperiaai.nl",
      },
      "areaServed": { "@type": "Country", "name": "Nederland" },
      "offers": service.tiers
        .filter((t) => t.price !== '—')
        .map((t) => ({
          "@type": "Offer",
          "name": t.label,
          "description": t.desc,
          "price": t.price.replace(/[^\d,]/g, '').replace(',', '.'),
          "priceCurrency": "EUR",
        })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": service.faq.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.keywords}
        canonical={`/${service.slug}`}
        schema={schema}
      />

      {/* ─── Hero ────────────────────────────────────────────── */}
      <section
        style={{
          background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
          padding: '110px 0 70px',
        }}
        data-testid="service-hero"
      >
        <div className="container-custom max-w-5xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-white/50 text-xs mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4A017] transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link to="/diensten" className="hover:text-[#D4A017] transition-colors">Diensten</Link>
            <ChevronRight size={12} />
            <span className="text-white/80">{service.h1.split('—')[0]?.trim()}</span>
          </nav>

          <div className="flex items-start gap-5 flex-col md:flex-row">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg, #D4A017, #fbbf24)' }}
            >
              <Icon size={26} style={{ color: '#0f172a' }} />
            </div>
            <div className="flex-1">
              <span
                style={{
                  fontSize: 11, color: '#D4A017', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                }}
              >
                Zoekintentie · {service.seoIntent}
              </span>
              <h1
                data-testid="service-h1"
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3 mb-5"
                style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.08 }}
              >
                {service.h1}
              </h1>
              <p className="text-white/70 text-xs md:text-sm mb-3 uppercase tracking-wide font-semibold">
                {service.hero.tagline}
              </p>
              <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-3xl mb-6">
                {service.hero.intro}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/register"
                  data-testid="service-cta-scan"
                  className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl transition-colors"
                >
                  Start gratis scan <ArrowRight size={16} />
                </Link>
                <Link
                  to="/prijzen"
                  data-testid="service-cta-pricing"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                >
                  Bekijk prijzen <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Why-blocks ──────────────────────────────────────── */}
      <section className="bg-white py-16" data-testid="service-why">
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <p
              style={{
                fontSize: 11, color: '#D4A017', textTransform: 'uppercase',
                letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8,
              }}
            >
              Waarom dit werkt
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0f172a]"
              style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
            >
              {service.body.whyTitle}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {service.body.whyBlocks.map((b, i) => (
              <div
                key={i}
                className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-6"
              >
                <div
                  className="w-9 h-9 rounded-lg bg-[#D4A017]/10 flex items-center justify-center mb-3 text-[#D4A017] font-bold text-sm"
                >
                  {i + 1}
                </div>
                <h3
                  className="text-lg font-bold text-[#0f172a] mb-2 leading-snug"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {b.title}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── What you get + Uitvoering ──────────────────────── */}
      <section className="bg-[#f8fafc] py-16" data-testid="service-what">
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* What */}
            <div>
              <p
                style={{
                  fontSize: 11, color: '#D4A017', textTransform: 'uppercase',
                  letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8,
                }}
              >
                Concreet
              </p>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-6"
                style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
              >
                {service.body.whatTitle}
              </h2>
              <ul className="space-y-3">
                {service.body.what.map((w, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-[#15803d] shrink-0 mt-0.5" />
                    <span className="text-[#0f172a] leading-relaxed">{w}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Uitvoering */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 lg:sticky lg:top-24 self-start">
              <div className="flex items-center gap-2 mb-3">
                <Bot size={18} className="text-[#D4A017]" />
                <p
                  style={{
                    fontSize: 11, color: '#D4A017', textTransform: 'uppercase',
                    letterSpacing: '0.12em', fontWeight: 700,
                  }}
                >
                  {service.body.uitvoeringTitle}
                </p>
              </div>
              <p className="text-[#0f172a] leading-relaxed mb-5">
                {service.body.uitvoering}
              </p>
              <div className="grid grid-cols-3 gap-2 pt-5 border-t border-slate-200">
                <div className="text-center">
                  <Bot size={20} className="text-[#15803d] mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-[#15803d] uppercase tracking-wide">Auto-push</p>
                  <p className="text-[10px] text-[#64748b]">WP · Shopify · Webflow</p>
                </div>
                <div className="text-center">
                  <Zap size={20} className="text-[#0369a1] mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-[#0369a1] uppercase tracking-wide">Snippet</p>
                  <p className="text-[10px] text-[#64748b]">Wix · Squarespace</p>
                </div>
                <div className="text-center">
                  <Headphones size={20} className="text-[#0e7490] mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-[#0e7490] uppercase tracking-wide">Team</p>
                  <p className="text-[10px] text-[#64748b]">Joomla · Drupal</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Tiers ──────────────────────────────────────────── */}
      <section className="bg-white py-16" data-testid="service-tiers">
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p
              style={{
                fontSize: 11, color: '#D4A017', textTransform: 'uppercase',
                letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8,
              }}
            >
              Inbegrepen in
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0f172a]"
              style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
            >
              Welke tier past bij u?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {service.tiers.map((t, i) => {
              const available = t.price !== '—';
              return (
                <div
                  key={i}
                  data-testid={`tier-${i}`}
                  className={`rounded-2xl p-6 border-2 transition-all ${
                    available
                      ? i === 2
                        ? 'bg-[#0f172a] border-[#D4A017] text-white'
                        : 'bg-white border-slate-200 hover:border-[#D4A017]'
                      : 'bg-[#f8fafc] border-slate-200 opacity-60'
                  }`}
                >
                  <p
                    className={`text-xs uppercase tracking-wide font-bold mb-2 ${
                      i === 2 && available ? 'text-[#D4A017]' : 'text-[#64748b]'
                    }`}
                  >
                    {t.label}
                  </p>
                  <p
                    className={`text-2xl font-extrabold mb-3 ${
                      i === 2 && available ? 'text-white' : 'text-[#0f172a]'
                    }`}
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    {t.price}
                  </p>
                  <p
                    className={`text-sm leading-snug ${
                      i === 2 && available ? 'text-white/80' : 'text-[#475569]'
                    }`}
                  >
                    {t.desc}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-[#94a3b8] mt-6">
            Alle prijzen excl. btw · Maandelijks opzegbaar · Geen lock-in. Managed AI: €995,95 launch-maand, daarna €2.495,95 p/m.
          </p>
          <div className="text-center mt-6">
            <Link
              to="/prijzen"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0f172a] hover:text-[#D4A017] transition-colors"
            >
              Volledige prijsstructuur <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FAQ ────────────────────────────────────────────── */}
      <section className="bg-[#f8fafc] py-16" data-testid="service-faq">
        <div className="container-custom max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <p
              style={{
                fontSize: 11, color: '#D4A017', textTransform: 'uppercase',
                letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8,
              }}
            >
              Veelgestelde vragen
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0f172a]"
              style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
            >
              Wat klanten vragen over {service.h1.split('—')[0]?.trim().toLowerCase()}
            </h2>
          </div>
          <div className="space-y-3">
            {service.faq.map((f, i) => (
              <details
                key={i}
                data-testid={`service-faq-${i}`}
                className="bg-white border border-slate-200 hover:border-[#D4A017] rounded-2xl p-5 group transition-colors"
              >
                <summary className="cursor-pointer font-bold text-[#0f172a] flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <ChevronRight
                    size={18}
                    className="text-[#D4A017] transition-transform group-open:rotate-90"
                  />
                </summary>
                <p className="text-sm text-[#475569] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Related services ──────────────────────────────── */}
      {related.length > 0 && (
        <section className="bg-white py-16" data-testid="service-related">
          <div className="container-custom max-w-6xl mx-auto px-4">
            <div className="text-center mb-10">
              <p
                style={{
                  fontSize: 11, color: '#D4A017', textTransform: 'uppercase',
                  letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8,
                }}
              >
                Combineer met
              </p>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0f172a]"
                style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
              >
                Andere relevante diensten
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((r) => {
                const RIcon = ICON_MAP[r.iconName] || Search;
                return (
                  <Link
                    key={r.slug}
                    to={`/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="bg-[#f8fafc] border border-slate-200 hover:border-[#D4A017] rounded-2xl p-6 transition-all hover:shadow-md group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#D4A017]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4A017]/20 transition-colors">
                      <RIcon size={18} className="text-[#D4A017]" />
                    </div>
                    <p className="text-xs uppercase tracking-wide font-semibold text-[#94a3b8] mb-1">
                      {r.seoIntent}
                    </p>
                    <h3 className="text-base font-bold text-[#0f172a] mb-2 leading-snug group-hover:text-[#D4A017] transition-colors">
                      {r.h1.split('—')[0]?.trim()}
                    </h3>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0f172a] group-hover:text-[#D4A017] transition-colors">
                      Lees meer <ArrowRight size={12} />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ─── Closing CTA ───────────────────────────────────── */}
      <section
        style={{
          background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
          padding: '70px 0',
        }}
        data-testid="service-closing-cta"
      >
        <div className="container-custom max-w-4xl mx-auto px-4 text-center">
          <Sparkles size={28} className="text-[#D4A017] mx-auto mb-4" />
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
          >
            Wilt u {service.seoIntent}?
          </h2>
          <p className="text-white/70 mb-7 max-w-2xl mx-auto leading-relaxed">
            Start met de gratis online groeiscan. Binnen 60 seconden krijgt u uw groeiscore, top-10 verbeterpunten en 3 directe groeikansen voor uw branche.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-7 py-3.5 rounded-xl transition-colors"
            >
              Start gratis scan <ArrowRight size={16} />
            </Link>
            <Link
              to="/diensten"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-7 py-3.5 rounded-xl transition-colors"
            >
              Bekijk alle diensten <ChevronRight size={16} />
            </Link>
          </div>
          <p className="text-white/40 text-xs mt-6">
            Geen creditcard nodig · Maandelijks opzegbaar · Geen lock-in
          </p>
        </div>
      </section>
    </>
  );
}
